import { useState } from 'react';
import { toast } from 'sonner';
import { Panel } from '../ui/Panel';
import { btnGhost } from '../ui/styles';
import { StaffAccess } from './StaffAccess';
import { useReauth } from '../useReauth';
import { revokeStaffLogin } from '../adminApi';
import { ApiError } from '../../core/api/http';
import { ROLES, isRoleId } from '../../core/rules/permissions';
import type { StaffLogin } from '../../core/types/adminRequests';
type Props = {
  logins: StaffLogin[];
  onLogin: (staffId: string, login: StaffLogin | null) => void;
};

/**
 * Logins zonder behandelaar erbij (bijv. Marketing): wel in het beheer, niet in het rooster of op
 * de website. Rol, einddatum, sessies en intrekken, net als bij een collega.
 */
export function OtherLogins({
  logins,
  onLogin
}: Props) {
  const {
    guard,
    dialog
  } = useReauth('Toegang aanpassen vraagt een extra bevestiging.');
  if (logins.length === 0) return null;
  return <Panel title="Andere logins" description="Mensen die in het beheer werken maar niet behandelen, zoals marketing. Ze staan niet in het rooster of op de website." data-id="element-4049" data-mp="i7zwgwbgqr24">
      {dialog}
      <ul className="divide-y divide-line" data-id="element-4050" data-mp="24668ri1ydjej">
        {logins.map(l => <OtherLoginRow key={l.staffId} login={l} onLogin={next => onLogin(l.staffId, next)} guard={guard} data-id="element-4051" data-mp="244v1v9d7nvj7" />)}
      </ul>
    </Panel>;
}
function OtherLoginRow({
  login,
  onLogin,
  guard
}: {
  login: StaffLogin;
  onLogin: (l: StaffLogin | null) => void;
  guard: ReturnType<typeof useReauth>['guard'];
}) {
  const [busy, setBusy] = useState(false);
  const role = login.roles?.find(isRoleId);
  const name = login.email;
  const revoke = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await guard(() => revokeStaffLogin(login.staffId));
      if (!res.done) return;
      onLogin(null);
      toast(`${name} kan niet meer inloggen`, {
        description: 'Ze is overal uitgelogd.'
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Intrekken lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  return <li className="py-4 first:pt-0 last:pb-0" data-id="element-4052" data-mp="ryl8u78r8nnf">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]" data-id="element-4053" data-mp="wwczyz1ks5qk">
        <span className="text-ink" data-id="element-4054" data-mp="iwc09mx1r4co">
          {login.email} <span className="text-muted" data-id="element-4055" data-mp="ixn57q28ai5d">· {role ? ROLES[role].name : 'geen rol'} · {login.status === 'uitgenodigd' ? 'uitgenodigd' : 'actief'}</span>
        </span>
        <button type="button" onClick={() => void revoke()} disabled={busy} aria-busy={busy} className={btnGhost} data-id="element-4056" data-mp="1anrlbciygj0s">
          {busy ? 'Intrekken…' : 'Login intrekken'}
        </button>
      </div>
      <StaffAccess key={`${(login.roles ?? []).join(',')}|${login.validUntil ?? ''}`} name={name} staffId={login.staffId} login={login} onLogin={onLogin} guard={guard} data-id="element-4057" data-mp="11td8dglwdkku" />
    </li>;
}